import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { api } from '../config/api';
import AnswerBox from '../components/AnswerBox';
import ConnectionsPuzzle from '../puzzles/ConnectionsPuzzle';
import InterseccionPuzzle from '../puzzles/InterseccionPuzzle';
import SandwichPuzzle from '../puzzles/SandwichPuzzle';
import ItsMozartKPuzzle from '../puzzles/ItsMozartKPuzzle';
import './PuzzlePage.css';

// Map of puzzle body templates to React components
const puzzleComponents = {
  'connections': ConnectionsPuzzle,
  'interseccion': InterseccionPuzzle,
  'sandwich': SandwichPuzzle,
  'itsmozartk': ItsMozartKPuzzle
};

function PuzzlePage() {
  const { slug } = useParams();
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solved, setSolved] = useState(false);

  useEffect(() => {
    async function fetchPuzzle() {
      try {
        const data = await api.getPuzzle(slug);
        setPuzzle(data.puzzle);
        setSolved(data.puzzle.solved || false);
      } catch (err) {
        console.error('Error fetching puzzle:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchPuzzle();
  }, [slug]);

  const handleCorrect = () => {
    setSolved(true);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading puzzle...</p>
        </div>
      </div>
    );
  }

  if (error || !puzzle) {
    return (
      <div className="container">
        <div className="error-state">
          <h2>Puzzle not found</h2>
          <p>{error || 'This puzzle does not exist.'}</p>
          <Link to="/puzzles" className="btn btn-primary mt-lg">
            Back to Puzzles
          </Link>
        </div>
      </div>
    );
  }

  // Get the puzzle body component
  const PuzzleBody = puzzleComponents[puzzle.body_template];

  return (
    <div className="puzzle-page">
      <div className="container">
        <Link to="/puzzles" className="back-link">
          <ArrowLeft size={18} />
          <span>Back to Puzzles</span>
        </Link>

        <div className="puzzle-header">
          <h1 className="puzzle-title">
            {puzzle.title}
            {solved && (
              <span className="solved-badge">
                <CheckCircle size={24} />
                Solved
              </span>
            )}
          </h1>
          {puzzle.flavor_text && (
            <p className="puzzle-flavor">{puzzle.flavor_text}</p>
          )}
        </div>

        <div className="puzzle-body">
          {PuzzleBody ? (
            <PuzzleBody />
          ) : (
            <div className="puzzle-placeholder">
              <p>Puzzle content not available.</p>
            </div>
          )}
        </div>
      </div>

      <AnswerBox 
        puzzleSlug={slug} 
        onCorrect={handleCorrect}
        disabled={solved}
      />
    </div>
  );
}

export default PuzzlePage;
